import { useEffect, useMemo } from "react";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { Plus, X } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { Button, Carte, Onglets, Select, type Onglet } from "@/shared/ui";
import { conversationByIdAtom } from "@/panels/conversations/atoms";
import { useCreatorsList } from "@/panels/creators";
import { PANEL_VISUALS } from "@/shell/PanelThumbnails";

import {
  focusedGroupIdAtom,
  layoutAtom,
  mobileActiveInstanceIdAtom,
  panelPickerOpenAtom,
} from "@/core/state";
import {
  flattenLayoutForMobile,
  removePanel,
  setPanelView,
  type PanelInstance,
} from "@/shell/layout";
import {
  panelRegistry,
  type PanelDefinition,
  type PanelId,
} from "@/shell/PanelRegistry";

import { PanelHost } from "./PanelHost";
import { MiniAvatar } from "./TabContent";

/** Le cadre d'un panneau, comme `TabGroup` le pose : une `Carte` qui garde les classes
 *  `cockpit-panel` (globals.css s'y accroche) sans leur contour gris ni leur triangle. */
const CADRE = "cockpit-panel flex h-full w-full flex-col border-[color:var(--sf-hairline)] after:hidden";

/**
 * Shell mobile : remplace LayoutRenderer quand `useIsMobile()` est true.
 * Render un seul TabGroup occupant tout l'ecran, alimente par le flatten DFS
 * du layout desktop sous-jacent. Pas de Split, pas de drag-dock, pas de
 * resize handles.
 *
 * Le state desktop (workspaces, layouts persistes) n'est PAS touche — on
 * lit le meme `layoutAtom` mais on l'aplatit a la volee pour le rendu.
 * Les mutations (addPanel, removePanel) ecrivent normalement dans
 * `layoutAtom`, mais sont coercees mobile-safe au site d'appel
 * (cf coerceMobileAddTarget).
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  cockpit-panel (full-screen)                │
 *   │  ┌────────────────────────────────────────┐ │
 *   │  │ TabStrip horizontal scrollable         │ │  ← MobileTabStrip
 *   │  ├────────────────────────────────────────┤ │
 *   │  │                                        │ │
 *   │  │ PanelHost(activeInstance)              │ │
 *   │  │                                        │ │
 *   │  └────────────────────────────────────────┘ │
 *   └─────────────────────────────────────────────┘
 */
export function MobileShell() {
  const layout = useAtomValue(layoutAtom);
  const [activeInstanceId, setActiveInstanceId] = useAtom(
    mobileActiveInstanceIdAtom,
  );

  const group = flattenLayoutForMobile(layout, activeInstanceId);
  const { panels, activeIndex } = group;

  // Si activeInstanceId est obsolete (instance supprimee), retomber sur le
  // premier panel disponible.
  useEffect(() => {
    if (panels.length === 0) {
      if (activeInstanceId !== null) setActiveInstanceId(null);
      return;
    }
    const stillExists = activeInstanceId
      ? panels.some((p) => p.id === activeInstanceId)
      : false;
    if (!stillExists) {
      setActiveInstanceId(panels[0]!.id);
    }
  }, [panels, activeInstanceId, setActiveInstanceId]);

  if (panels.length === 0) {
    return <MobileEmptyState />;
  }

  const activePanel = panels[activeIndex] ?? panels[0]!;

  return (
    <Carte remplissage="aucun" className={CADRE}>
      <MobileTabStrip
        panels={panels}
        activeInstanceId={activePanel.id}
        onSelect={(id) => setActiveInstanceId(id)}
      />
      <div className="cockpit-panel-body relative">
        <PanelHost instance={activePanel} />
      </div>
    </Carte>
  );
}

/* ============================================================ */
/* MobileTabStrip                                                */
/* ============================================================ */

interface MobileTabStripProps {
  panels: readonly PanelInstance[];
  activeInstanceId: string;
  onSelect: (instanceId: string) => void;
}

/**
 * Les conversations des onglets fan, lues en direct : nom, photo, non lus. UN atome dérivé par
 * liste d'ids — il ne se réévalue que quand l'une de CES conversations change (pas à chaque
 * heartbeat de la liste, cf `conversationByIdAtom`).
 */
function useConversationsDesOnglets(ids: readonly string[]) {
  const cle = ids.join("|");
  const lues = useMemo(
    () => atom((get) => cle.split("|").filter(Boolean).map((id) => [id, get(conversationByIdAtom(id))] as const)),
    [cle],
  );
  return new Map(useAtomValue(lues));
}

/** La photo d'une créatrice, lue en direct (monté seulement pour un onglet creator-detail). */
function PhotoDeCreatrice({ slug }: { slug: string }) {
  const creator = useCreatorsList().find((c) => c.slug === slug);
  if (!creator) return null;
  return <MiniAvatar src={creator.avatar_url ?? null} seed={creator.id} taille={20} />;
}

/**
 * Bandeau d'onglets mobile — les `Onglets` de la librairie (style ligne) : la rangée défile
 * à l'horizontale sous le pouce, l'actif en texte plein avec le trait de la marque. Photo ou
 * icône devant le nom, non lus en pastille (`compte`). Pas de glisser-déposer ici, et les
 * boutons + et × (cibles tactiles de 44 px) restent à droite.
 *
 * Differences vs TabStrip desktop :
 *   - Pas de drag-dock (DraggableTab non utilise)
 *   - Pas de wrap : la rangée défile
 *   - Close button toujours visible (pas de hover sur mobile)
 *   - Le sélecteur de vue est un `Select` compact
 */
function MobileTabStrip({
  panels,
  activeInstanceId,
  onSelect,
}: MobileTabStripProps) {
  const { t } = useTranslation();
  const setLayout = useSetAtom(layoutAtom);
  const setFocusedGroupId = useSetAtom(focusedGroupIdAtom);
  const setPickerOpen = useSetAtom(panelPickerOpenAtom);
  const activeDef: PanelDefinition | undefined = (() => {
    const active = panels.find((p) => p.id === activeInstanceId);
    return active
      ? panelRegistry[active.panelId as PanelId]
      : undefined;
  })();
  const activeInstance = panels.find((p) => p.id === activeInstanceId);
  const fans = useConversationsDesOnglets(
    panels.flatMap((p) => (p.panelId === "fan" && p.params?.["conversationId"] ? [p.params["conversationId"] as string] : [])),
  );

  function openPicker() {
    // En mobile, on ne pre-cible pas un group desktop specifique : on laisse
    // addPanel utiliser `active-group` (= premier non-vide DFS, ou injection
    // a la racine si layout vide). Le panel apparaitra automatiquement dans
    // le strip mobile au prochain render (flatten DFS).
    //
    // PAS poser mobileRootGroupId() ici : c'est l'ID du TabGroup VIRTUEL
    // produit par flattenLayoutForMobile, qui n'existe pas dans le vrai
    // layoutAtom. PanelPicker passerait alors `{ kind: "group", groupId }`
    // a addPanel, qui ne trouverait jamais le group et silent-fail (pas de
    // throw, donc le try/catch fallback ne se declenche pas).
    setFocusedGroupId(null);
    setPickerOpen(true);
  }

  function closeActive() {
    if (!activeInstance) return;
    setLayout((prev) => removePanel(prev, activeInstance.id));
  }

  const options: Onglet[] = panels.map((p) => {
    const pDef = panelRegistry[p.panelId as PanelId];
    const title = p.title ?? (pDef ? t(`panels.registry.${pDef.id}.title`) : p.panelId);
    const conversationId = p.params?.["conversationId"] as string | undefined;
    const creatorSlug = p.params?.["creatorSlug"] as string | undefined;
    if (p.panelId === "fan" && conversationId) {
      // Parite desktop : photo + nom vivant + pastille de non lus.
      const conv = fans.get(conversationId);
      return {
        valeur: p.id,
        etiquette: conv?.fan_name || title,
        icone: conv ? <MiniAvatar src={conv.fan_avatar_url ?? null} seed={conv.id} taille={20} /> : undefined,
        compte: conv && conv.unread_count > 0 ? conv.unread_count : undefined,
      };
    }
    if (p.panelId === "creator-detail" && creatorSlug) {
      return { valeur: p.id, etiquette: title, icone: <PhotoDeCreatrice slug={creatorSlug} /> };
    }
    const Icone = PANEL_VISUALS[p.panelId as keyof typeof PANEL_VISUALS]?.icon;
    return { valeur: p.id, etiquette: title, icone: Icone ? <Icone className="h-4 w-4" aria-hidden /> : undefined };
  });

  return (
    <header className="cockpit-panel-header items-center border-b-fg/[0.07] px-0">
      <div className="min-w-0 flex-1">
        {/* La hairline sous les onglets, c'est celle de l'en-tête : celle des `Onglets` est tue. */}
        <Onglets
          style="ligne"
          taille="lg"
          nom={t("shell.tabStrip.tabsAria")}
          options={options}
          valeur={activeInstanceId}
          onChange={onSelect}
          className="[&_.og-ligne]:border-b-0"
        />
      </div>

      <div className="flex shrink-0 items-center gap-1 pr-1">
        {activeDef && activeDef.views.length > 1 && activeInstance && (
          <MobileViewPicker
            def={activeDef}
            activeViewId={activeInstance.viewId ?? activeDef.defaultViewId}
            onChange={(viewId) =>
              setLayout((prev) =>
                setPanelView(prev, activeInstance.id, viewId),
              )
            }
          />
        )}
        <Button
          variant="ghost"
          size="lg"
          iconOnly
          className="h-12 w-12"
          onClick={openPicker}
          aria-label={t("shell.tabStrip.addPanel")}
          icon={<Plus className="h-5 w-5" />}
        />
        {activeInstance && panels.length > 1 && (
          <Button
            variant="ghost"
            size="lg"
            iconOnly
            className="h-12 w-12"
            onClick={closeActive}
            aria-label={t("shell.tabStrip.closePanel")}
            icon={<X className="h-5 w-5" />}
          />
        )}
      </div>
    </header>
  );
}

/* ============================================================ */
/* MobileViewPicker — version condensee                          */
/* ============================================================ */

interface MobileViewPickerProps {
  def: PanelDefinition;
  activeViewId: string;
  onChange: (viewId: string) => void;
}

function MobileViewPicker({
  def,
  activeViewId,
  onChange,
}: MobileViewPickerProps) {
  const { t } = useTranslation();
  return (
    <Select
      value={activeViewId}
      onChange={(e) => onChange(e.target.value)}
      taille="sm"
      pilule
      aria-label={t("shell.tabStrip.viewAria")}
      className="max-w-[7rem]"
    >
      {def.views.map((v) => (
        <option key={v.id} value={v.id}>
          {v.name}
        </option>
      ))}
    </Select>
  );
}

/* ============================================================ */
/* MobileEmptyState                                              */
/* ============================================================ */

function MobileEmptyState() {
  const { t } = useTranslation();
  const setPickerOpen = useSetAtom(panelPickerOpenAtom);
  const setFocusedGroupId = useSetAtom(focusedGroupIdAtom);

  function openPicker() {
    // Cf MobileTabStrip.openPicker pour la rationale (null = active-group,
    // pas mobileRootGroupId qui n'existe pas dans le vrai layout).
    setFocusedGroupId(null);
    setPickerOpen(true);
  }

  return (
    <Carte remplissage="aucun" className={CADRE}>
      <div className="cockpit-panel-body relative flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Carte onClick={openPicker} remplissage="lg" className="flex w-auto flex-col items-center gap-2 px-8 text-muted">
          <Plus className="h-8 w-8" />
          <span className="text-sm font-medium text-fg">{t("shell.tabStrip.addPanel")}</span>
        </Carte>
      </div>
    </Carte>
  );
}
